import { Injectable } from '@angular/core';
import { GraphqlService } from '@core/graphql/graphql.service';
import { Endpoint } from '@core/graphql/models/enums/endpoint.enum';
import { GraphqlMutationResponse } from '@core/graphql/models/graphql-mutation-response.model';
import { GraphqlPaginatedResponse } from '@core/graphql/models/graphql-paginated-response.model';
import { Observable, filter, first, interval, lastValueFrom, map, of, switchMap, timeout } from 'rxjs';
import {
    ServiceCreateInterface,
    ServiceInterface,
    ServiceStatus,
    ServiceType,
    ServiceUpdateInterface
} from './models/service.interface';

import servicesGqlModel from './graphql/models/service.model.graphql';
import createServiceGqlMutation from './graphql/mutations/create-service.mutation.graphql';
import updateServiceGqlMutation from './graphql/mutations/update-service.mutation.graphql';
import resultModelGql from '@core/graphql/query-models/result.model.graphql';
import getServiceGqlQuery from './graphql/queries/get-service.query.graphql';
import getServicesGqlQuery from './graphql/queries/get-services.query.graphql';
import getAvailableServicesGqlQuery from './graphql/queries/get-available-services.query.graphql';
import getAllContractorServicesGqlQuery from './graphql/queries/get-all-contractor-services.graphql';
import { WebsiteInterface } from '@core/website';
import { ServiceConfigService } from '@core/services/service-config.service';

export interface ServiceIdentity {
    contractorId: number;
    websiteId: number;
    type: ServiceType;
}

@Injectable({
    providedIn: 'root'
})
export class ServiceService {
    constructor(
        private readonly graphqlService: GraphqlService,
        private readonly serviceStateService: ServiceConfigService
    ) {

    }

    public getServices(contractorId: number, websiteId: number): Observable<ServiceInterface[]> {
        const query = getServicesGqlQuery + servicesGqlModel;

        return this.graphqlService.query<{ contractor: { website: { services: GraphqlPaginatedResponse<ServiceInterface>; }; }; }>(
            Endpoint.GatewayApi,
            query,
            { contractorId, websiteId }
        ).pipe(
            map(res => {
                return res.contractor.website.services.items.map(service => ({
                    name: this.serviceStateService.getName(service.type),
                    description: this.serviceStateService.getDescription(service.type),
                    logo: this.serviceStateService.getLogo(service.type),
                    state: service.state,
                    currentState: this.serviceStateService.getCurrentState(service.state, service.type),
                    type: service.type,
                    status: service.status,
                    config: service.config,
                    contractorPaymentMethodId: service.contractorPaymentMethodId,
                    createdAt: service.createdAt
                }));
            })
        );
    }

    public getAvailableServices(contractorId: number, websiteId: number): Observable<ServiceType[]> {
        const query = getAvailableServicesGqlQuery;

        return this.graphqlService.query<{ contractor: { website: { availableServices: ServiceType[]; }; }; }>(
            Endpoint.GatewayApi,
            query,
            { contractorId, websiteId }
        ).pipe(
            map(res => res.contractor.website.availableServices)
        );
    }

    public getAllContractorServices(contractorId: number): Observable<{
        type: ServiceType;
        website: Pick<WebsiteInterface, 'name' | 'id'>;
    }[]> {
        const query = getAllContractorServicesGqlQuery;

        return this.graphqlService.query<{
            contractor: {
                websites: {
                    items: {
                        services: {
                            items: {
                                type: ServiceType;
                                website: Pick<WebsiteInterface, 'name' | 'id'>;
                            }[];
                        };
                    }[];
                };
            };
        }>(
            Endpoint.GatewayApi,
            query,
            { contractorId }
        ).pipe(
            map(res => res.contractor?.websites.items.flatMap(item => item.services.items))
        );
    }

    public getService<T = never>(contractorId: number, websiteId: number, type: ServiceType): Observable<ServiceInterface<T> | null> {
        const query = getServiceGqlQuery + servicesGqlModel;

        return this.graphqlService.query<{ contractor: { website: { service: ServiceInterface<T>; }; }; }>(
            Endpoint.GatewayApi,
            query,
            { contractorId, websiteId, type }
        ).pipe(
            map(res => {
                const service = res.contractor?.website?.service;

                return service
                    ? {
                        name: this.serviceStateService.getName(service.type),
                        state: service.state,
                        currentState: this.serviceStateService.getCurrentState(service.state, service.type),
                        type: service.type,
                        status: service.status,
                        config: service.config,
                        contractorPaymentMethodId: service.contractorPaymentMethodId,
                        createdAt: service.createdAt
                    }
                    : null;
            })
        );
    }

    public createService<T>(service: ServiceCreateInterface<T>): Promise<boolean> {
        const query = createServiceGqlMutation + resultModelGql;

        return lastValueFrom(this.graphqlService.mutation<{ contractor: { website: { service: { create: GraphqlMutationResponse; }; }; }; },
            ServiceCreateInterface<T>>(
                Endpoint.GatewayApi,
                query,
                service
            ).pipe(
                map(res => res.contractor.website.service.create.status)
            )
        );
    }

    public updateService<T>(service: ServiceUpdateInterface<T>): Promise<boolean> {
        const query = updateServiceGqlMutation + resultModelGql;

        return lastValueFrom(this.graphqlService.mutation<{ contractor: { website: { service: { update: GraphqlMutationResponse; }; }; }; },
            ServiceUpdateInterface<T>>(
                Endpoint.GatewayApi,
                query,
                service
            ).pipe(
                map(res => res.contractor.website.service.update.status)
            )
        );
    }

    public ensureServiceStatus(
        service: ServiceIdentity,
        statuses: ServiceStatus[],
        options?: {
            timeout?: number;
            interval?: number;
        }
    ): Promise<boolean> {
        // First we create interval event with specific interval value
        const query = interval(options?.interval ?? 1000).pipe(
            // Then each tick we switch to request for service with specific params
            switchMap(() => this.getService(service.contractorId, service.websiteId, service.type)),
            // Then we block stream if service status is different than requested
            filter(service => statuses.includes(service?.status as ServiceStatus)),
            // In this plce we receive service with required staus
            map(() => true),
            // There we configure timeout. If none value came there in specisic timeout pipe'll emit false
            timeout({ with: () => of(false), first: options?.timeout ?? 10000 }),
            // And at the end we take first value. there we always receive true or false
            first()
        );

        return lastValueFrom(query);
    }
}
